/* ============================================================
   Palpites: preço e calorias

   Duas coisas diferentes que a tela costuma confundir:

     - o TROFÉU vai para quem chegou mais perto. É comparativo, sempre
       existe alguém, e não vale ponto.
     - a CRAVADA é acertar. Pode não haver nenhuma, e é a única forma de
       ganhar ponto no prato de outra pessoa.
   ============================================================ */

export interface Guess {
  uid: string;
  value: number;
}

/**
 * Quem chegou mais perto. Devolve TODOS os empatados: dois palpites iguais
 * são a mesma vitória, e premiar só o primeiro da lista é arbitrário.
 */
export function closestGuesses(guesses: readonly Guess[], actual: number): Guess[] {
  if (!guesses.length || !isFinite(actual)) return [];
  const distances = guesses.map((g) => Math.abs(g.value - actual));
  const best = Math.min(...distances);
  return guesses.filter((_, i) => distances[i] === best);
}

/**
 * Cravou?
 *
 * A tolerância existe porque acertar caloria na unidade é quase impossível:
 * com margem de 25, um chute de 700 crava um prato de 690. Sem ela o prêmio
 * nunca sairia e a regra viraria decoração.
 *
 * O epsilon é só para 32,10 − 32,10 não dar 0,0000001 em ponto flutuante.
 */
export function isExact(guess: number, actual: number, tolerance = 0): boolean {
  if (!isFinite(guess) || !isFinite(actual)) return false;
  return Math.abs(guess - actual) <= tolerance + 0.001;
}

export function exactGuesses(
  guesses: readonly Guess[],
  actual: number,
  tolerance = 0,
): Guess[] {
  return guesses.filter((g) => isExact(g.value, actual, tolerance));
}

/**
 * O teto de cada número que alguém digita.
 *
 * Sem teto, um chute de 5.000.000.000 kcal entrava, virava o "mais longe"
 * da lista e esticava a régua de todo mundo. O mesmo número mora na regra
 * do Firestore (que não importa nada e repete o literal) e na função de
 * correção de calorias: um prato de 10.000 kcal já é um banquete, e um de
 * R$ 5.000 é erro de digitação.
 */
export const TETO_DO_PALPITE = { kcal: 10000, price: 5000 } as const;

export type TipoDePalpite = keyof typeof TETO_DO_PALPITE;

/**
 * Lê o que a pessoa digitou. Aceita vírgula. Devolve o número ou o motivo
 * em português, para as duas telas dizerem a mesma coisa.
 */
export function lerPalpite(
  tipo: TipoDePalpite, texto: string,
): { ok: true; valor: number } | { ok: false; erro: string } {
  const limpo = texto.trim().replace(/\s/g, "").replace(",", ".");
  if (!limpo) {
    return { ok: false, erro: tipo === "kcal" ? "Dá um valor de calorias antes." : "Dá um preço antes." };
  }
  const valor = Number(limpo);
  if (!Number.isFinite(valor) || valor < 0) {
    return { ok: false, erro: tipo === "kcal" ? "Valor de calorias inválido." : "Preço inválido." };
  }
  const teto = TETO_DO_PALPITE[tipo];
  if (valor > teto) {
    return {
      ok: false,
      erro: tipo === "kcal"
        ? `Até ${teto.toLocaleString("pt-BR")} kcal. Nenhum prato passa disso.`
        : `Até R$ ${teto.toLocaleString("pt-BR")}. Confere o valor.`,
    };
  }
  return { ok: true, valor: tipo === "kcal" ? Math.round(valor) : Math.round(valor * 100) / 100 };
}
